import { type Locale, toStrapiLocale } from "@lib/i18n/locale";
import strapi, { type StrapiQuery } from "@lib/strapi/rest/client";
import {
  type ArticleTeaser,
  TEASER_FIELDS,
  TEASER_POPULATE,
  toTeasers,
} from "@modules/article/lib/teasers";

export const SEARCH_LIMIT = 24;

const searchArticles = async ({
  query,
  locale,
  category,
  signal,
}: {
  query: string;
  locale: Locale;
  category: string | null;
  signal?: AbortSignal;
}): Promise<ArticleTeaser[]> => {
  const search = await strapi.GET("/article-search", {
    params: {
      query: { query, locale: toStrapiLocale(locale), limit: SEARCH_LIMIT },
    },
    signal,
  });

  if (!search.data) {
    throw new Error(`Article search failed: ${search.response?.status}`);
  }

  const ranks = new Map(
    search.data.results.map(({ slug }, index) => [slug, index]),
  );

  if (!ranks.size) {
    return [];
  }

  const teaserQuery: StrapiQuery = {
    locale: toStrapiLocale(locale),
    pagination: { start: 0, limit: SEARCH_LIMIT },
    filters: {
      slug: { $in: [...ranks.keys()] },
      ...(category ? { categories: { slug: { $eq: category } } } : {}),
    },
    fields: TEASER_FIELDS,
    populate: TEASER_POPULATE,
  };

  const articles = await strapi.GET("/articles", {
    params: { query: teaserQuery },
    signal,
  });

  if (articles.error) {
    throw new Error(
      `Strapi did not answer for the search hits: ${JSON.stringify(articles.error)}`,
    );
  }

  return toTeasers(articles.data?.data ?? []).sort(
    (a, b) => ranks.get(a.slug)! - ranks.get(b.slug)!,
  );
};

export default searchArticles;
